<template>
  <Tab :tab="tab" wrapper-class-content="gap-8">
    <template #header-action>
      <button
        class="btn btn-primary gap-2 mb-0"
        @click="() => openNewAdminModal()"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          class="w-5 h-5"
        >
          <path
            d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z"
          />
        </svg>
        <span>New Admin</span>
      </button>
    </template>

    <template>
      <div class="grid grid-cols-2 gap-4 w-full">
        <div
          v-for="admin in admins"
          :key="admin.id"
          class="col-span-1 flex justify-between items-center px-7 py-5 hover:bg-truegray-50 border border-truegray-300 rounded"
        >
          <!-- Details -->
          <div class="flex flex-col gap-1">
            <p class="text-sm text-truegray-600">{{ admin.name }}</p>

            <p class="text-sm text-truegray-600">{{ admin.email }}</p>

            <p class="text-truegray-400 text-sm">
              {{ admin.job_title }}
            </p>

            <!-- Role -->
            <p class="text-truegray-400 text-sm">
              {{ ROLE_LABELS[admin.role] }}
            </p>
            <!-- /Role -->

            <!-- Connections email receiver -->
            <div
              v-if="admin.enable_connections_emails"
              class="flex items-center gap-2 text-truegray-400 text-xs"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                class="w-3 h-3"
              >
                <path
                  d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z"
                />
              </svg>

              Connections admin
            </div>
            <!-- /Connections email receiver -->

            <!-- Public contact details -->
            <div
              v-if="admin.enable_public_contact"
              class="flex items-center gap-2 text-truegray-400 text-xs"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                class="w-3 h-3"
              >
                <path
                  d="M3 4a2 2 0 0 0-2 2v1.161l8.441 4.221a1.25 1.25 0 0 0 1.118 0L19 7.162V6a2 2 0 0 0-2-2H3Z"
                />
                <path
                  d="m19 8.839-7.77 3.885a2.75 2.75 0 0 1-2.46 0L1 8.839V14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8.839Z"
                />
              </svg>

              Public contact details
            </div>
            <!-- /Public contact details -->
          </div>
          <!-- /Details -->

          <!-- Actions -->
          <div class="flex gap-5">
            <!-- Edit -->
            <div
              title="Edit admin"
              class="flex items-center group text-sm text-truegray-300 hover:text-green-400 cursor-pointer transition-all duration-100"
              @click="editAdmin(admin.id)"
            >
              <div
                v-if="adminLoading === admin.id"
                class="rounded-full bg-green-400 w-6 h-6"
              >
                <lottie :options="LOADING_ANIMATION_OPTIONS" />
              </div>
              <div v-else class="flex items-center gap-4">
                <!-- <div>Edit admin</div> -->
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="w-6 h-6"
                >
                  <path
                    d="m5.433 13.917 1.262-3.155A4 4 0 0 1 7.58 9.42l6.92-6.918a2.121 2.121 0 0 1 3 3l-6.92 6.918c-.383.383-.84.685-1.343.886l-3.154 1.262a.5.5 0 0 1-.65-.65Z"
                  />
                  <path
                    d="M3.5 5.75c0-.69.56-1.25 1.25-1.25H10A.75.75 0 0 0 10 3H4.75A2.75 2.75 0 0 0 2 5.75v9.5A2.75 2.75 0 0 0 4.75 18h9.5A2.75 2.75 0 0 0 17 15.25V10a.75.75 0 0 0-1.5 0v5.25c0 .69-.56 1.25-1.25 1.25h-9.5c-.69 0-1.25-.56-1.25-1.25v-9.5Z"
                  />
                </svg>
              </div>
            </div>
            <!-- /Edit -->

            <!-- Delete -->
            <div
              title="Remove admin"
              class="group flex items-center text-sm text-truegray-300 hover:text-red-600 cursor-pointer transition-all duration-100"
              @click="deleteAdmin(admin.id)"
            >
              <div
                v-if="adminLoading === admin.id"
                class="rounded-full bg-red-600 w-6 h-6"
              >
                <lottie :options="LOADING_ANIMATION_OPTIONS" />
              </div>
              <div v-else class="flex items-center gap-4">
                <!-- <div>Remove admin</div> -->
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  class="w-6 h-6"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <!-- /Delete -->
          </div>
          <!-- /Actions -->
        </div>
      </div>
    </template>
  </Tab>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { useStore } from '../../../../../js/store'
import * as loadingAnimation from '../../../../../shared/components/loading.json'
import Tab from './slots/Tab.vue'

export default defineComponent({
  name: 'TabAdmins',

  components: {
    Tab,
  },

  props: {
    tab: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const LOADING_ANIMATION_OPTIONS = {
      animationData: loadingAnimation.default,
    }
    const store = useStore()

    const ROLE_LABELS = {
      2: 'Full Access',
      3: 'Connections Admin',
    }

    const adminLoading = ref(null)
    const admins = computed(() => store.getters['shared/getAdmins'])

    const editAdmin = (id) => {
      openNewAdminModal(id)
    }

    const deleteAdmin = async (id) => {
      adminLoading.value = id
      await store.dispatch('careerwise/dispatchDeleteAdmin', id)
      adminLoading.value = null
    }

    const openNewAdminModal = (id = null) => {
      EventBus.$emit('showModal', {
        action: 'cw-upsert-admin',
        data: {
          id,
        },
      })
    }

    return {
      LOADING_ANIMATION_OPTIONS,
      ROLE_LABELS,
      adminLoading,
      admins,
      editAdmin,
      deleteAdmin,
      openNewAdminModal,
    }
  },
})
</script>
